import type { StoreOrderStep } from '@/dealers/_gen/gql'
import AccountMachine from '@/gf/modules/AccountMachine'
import AdditionalCharge from '@/gf/modules/AdditionalCharge'
import DiscountM from '@/gf/modules/Discount'
import Invoice from '@/gf/modules/Invoice'
import Money from '@/gf/modules/Money'
import Refund from '@/gf/modules/Refund'
import ShipmentItem from '@/gf/modules/ShipmentItem'
import StoreOrderEvent from '@/gf/modules/StoreOrderEvent'
import Time from '@/gf/modules/Time'
import Vendor from '@/gf/modules/Vendor'
import type { StoreOrderPayload } from '@/suppliers/types'
import { Organization } from '@/types'
import { QueryHookOptions, gql, useQuery } from '@apollo/client'

type LineItemPayload = StoreOrderPayload['lineItems'][number] & { rfqPart: { mpn: string } | null }

type Payload = Omit<StoreOrderPayload, 'payOnInvoice' | 'lineItems' | 'order'> & {
  step: StoreOrderStep
  lineItems: LineItemPayload[]
  stripePayments: boolean
  completedAt: string | null
  quickOrder: boolean
  order: Omit<StoreOrderPayload['order'], 'user'> & {
    user: {
      id: string
      name: string
      email: string
      phoneNumber: string | null
      organization: Organization
    }
  }
}

interface Data {
  storeOrder: Payload | null
}

interface Vars {
  id: string
}

const query = gql`
  query FetchStoreOrder($id: ID!) {
    storeOrder(id: $id) {
      id
      token
      shortId
      insertedAt
      updatedAt
      state
      step
      deliveryMethod
      paymentMethod
      paymentStatus
      stripePayments
      completedAt
      quickOrder
      shippingAmount {
        amount
        currency
      }
      salesTax {
        amount
        currency
      }
      total {
        amount
        currency
      }
      payoutRate
      convenienceFeeRate
      gearflowShipping
      purchaseOrder
      timingDetails
      pickup
      pickupAddress {
        lineOne
        lineTwo
        city
        state
        country
        postalCode
      }
      curriDeliveries {
        curriDeliveryId
        trackingUrl
        price {
          amount
          currency
        }
      }
      shippingLocation {
        id
        code
      }
      shippingAddress {
        firstName
        lastName
        lineOne
        lineTwo
        city
        state
        country
        postalCode
        companyName
        deliverable
        rdi
      }
      vendor {
        accountNumbers
        contactName
        emailAddress
        phoneNumber
      }
      accountMachines {
        id
        name
        serialNumber
        owned
        engineMake
        engineModel
        engineSerialNumber
        machine {
          make
          model
          year
        }
      }
      store {
        id
        name
        address {
          firstName
          lastName
          lineOne
          lineTwo
          city
          state
          country
          postalCode
          companyName
        }
        email
        phoneNumber
        shipsFree
        gearflowPaymentsEnabled
      }
      order {
        id
        name
        shortId
        insertedAt
        updatedAt
        state
        phoneNumber
        email
        comment
        user {
          id
          name: displayName
          email
          phoneNumber
          organization {
            id
            name
          }
        }
        billingAddress {
          firstName
          lastName
          lineOne
          lineTwo
          city
          state
          country
          postalCode
          companyName
        }
        requestForQuote {
          id
          shortId
          fullName
          phoneNumber
          emailAddress
          partsRequest
          machineInformation
          shippingOption
          user {
            id
            name: displayName
            organization {
              id
              name
            }
          }
          brokeredByRequestForQuotes {
            id
          }
        }
      }
      balanceTransaction {
        id
      }
      additionalCharges {
        id
        name
        price {
          amount
          currency
        }
        state
        invoice {
          id
          dueDate
          isPaid
          isCanceled
        }
      }
      discounts {
        percent
      }
      refunds {
        id
        amount {
          amount
          currency
        }
      }
      lineItems {
        id
        quantity
        externalId
        taskNumber
        suggestion
        inStock
        availableAt
        rfqPart {
          mpn
        }
        product {
          id
          name
          mpn
          altMpn
          salePrice
          images {
            variants {
              name
              url
            }
          }
        }
        unitPrice {
          amount
          currency
        }
        discountedUnitPrice {
          amount
          currency
        }
        extendedPrice {
          amount
          currency
        }
        shipmentItems {
          id
          quantity
          shipment {
            id
            link
            carrier
            tracking
            shippedAt
            receivedAt
            label {
              id
              url
              cost {
                amount
                currency
              }
              shipDate
            }
          }
        }
      }
      events {
        id
        type
        privacy
        user {
          id
          name: displayName
        }
        insertedAt
        transaction {
          id
          action
          customer {
            amount
            currency
          }
          gearflow {
            amount
            currency
          }
          supplier {
            amount
            currency
          }
        }
        refund {
          id
          amount {
            amount
            currency
          }
        }
        additionalCharge {
          id
          name
          price {
            amount
            currency
          }
        }
        shipment {
          id
          link
          carrier
          tracking
          shipmentItems {
            id
            quantity
            lineItem {
              id
              productName
              quantity
            }
          }
        }
        storeOrderAudit {
          id
          shippingCost {
            amount
            currency
          }
          timingDetails
          pickup
          taxCost {
            amount
            currency
          }
          lineItemAudits {
            id
            quantity
            unitPrice {
              amount
              currency
            }
            productId
            productName
            productMpn
          }
        }
        diffStoreOrderAudit {
          id
          shippingCost {
            amount
            currency
          }
          timingDetails
          pickup
          taxCost {
            amount
            currency
          }
          lineItemAudits {
            id
            quantity
            unitPrice {
              amount
              currency
            }
            productId
            productName
            productMpn
          }
        }
        note
        emailSent
        data {
          id
          details
          payOnAccount
          payOnInvoice
          deliveryMethod
          previousAddress {
            firstName
            lastName
            lineOne
            lineTwo
            city
            state
            country
            postalCode
            companyName
          }
        }
      }
      features {
        txns
        storeOrderTimelineV1
      }
      buyerNotificationUsers {
        id
        name
      }
      invoices {
        id
        link
        dueDate
        isPaid
        isCanceled
        isFinanced
        stripeInvoiceId
      }
      branch {
        billingAddress {
          companyName
          firstName
          lastName
          lineOne
          lineTwo
          city
          state
          postalCode
          country
        }
      }
      problems {
        id
        type
        details
        resolvedAt
      }
      stripePaymentIntentId
    }
  }
`

const useFetchStoreOrder = (options?: QueryHookOptions<Data, Vars>) => {
  const result = useQuery<Data, Vars>(query, { ...options, fetchPolicy: 'no-cache' })

  const data = result.data && {
    ...result.data,
    storeOrder: result.data.storeOrder && {
      ...result.data.storeOrder,
      order: {
        ...result.data.storeOrder.order,
        customer: {
          name: result.data.storeOrder.order.name || '',
          email: result.data.storeOrder.order.email,
          phoneNumber: result.data.storeOrder.order.phoneNumber,
          user: result.data.storeOrder.order.user,
        },
      },
      shippingAmount:
        result.data.storeOrder.shippingAmount &&
        Money.fromPayload(result.data.storeOrder.shippingAmount),
      salesTax:
        result.data.storeOrder.salesTax && Money.fromPayload(result.data.storeOrder.salesTax),
      lineItems: result.data.storeOrder.lineItems.map((lineItemPayload) => ({
        ...lineItemPayload,
        unitPrice: Money.fromPayload(lineItemPayload.unitPrice),
        discountedUnitPrice: Money.fromPayload(lineItemPayload.discountedUnitPrice),
        extendedPrice: Money.fromPayload(lineItemPayload.extendedPrice),
        shipmentItems:
          lineItemPayload.shipmentItems &&
          lineItemPayload.shipmentItems.map((shipmentItem) =>
            ShipmentItem.fromPayload(shipmentItem)
          ),
        availableAt:
          lineItemPayload.availableAt !== null
            ? Time.fromPayload(lineItemPayload.availableAt)
            : null,
      })),
      additionalCharges:
        result.data.storeOrder.additionalCharges &&
        result.data.storeOrder.additionalCharges.map(AdditionalCharge.fromPayload),
      refunds:
        result.data.storeOrder.refunds && result.data.storeOrder.refunds.map(Refund.fromPayload),
      discounts:
        result.data.storeOrder.discounts &&
        result.data.storeOrder.discounts.map(DiscountM.fromPayload),
      events:
        result.data.storeOrder.events &&
        result.data.storeOrder.events.map(StoreOrderEvent.fromPayload),
      insertedAt: Time.fromPayload(result.data.storeOrder.insertedAt),
      updatedAt: Time.fromPayload(result.data.storeOrder.updatedAt),
      completedAt:
        result.data.storeOrder.completedAt !== null
          ? Time.fromPayload(result.data.storeOrder.completedAt)
          : null,
      accountMachines:
        result.data.storeOrder.accountMachines &&
        result.data.storeOrder.accountMachines.map((accountMachinePayload) =>
          AccountMachine.fromPayload(accountMachinePayload)
        ),
      vendor: result.data.storeOrder.vendor
        ? Vendor.fromPayload(result.data.storeOrder.vendor)
        : undefined,
      invoices: result.data.storeOrder.invoices
        ? result.data.storeOrder.invoices.map((invoice) => Invoice.fromPayload(invoice))
        : undefined,
      total: Money.fromPayload(result.data.storeOrder.total),
    },
  }

  return { ...result, data }
}

export type StoreOrder = Exclude<
  Exclude<ReturnType<typeof useFetchStoreOrder>['data'], undefined>['storeOrder'],
  null
>

export default useFetchStoreOrder
